import { getIntersectionRoot, getScrollEventTarget, getScrollTop, scrollContainerMediaQuery } from '@theme/scroll-container';

const HIDDEN = 'store-header-group--hidden';
const STICKY = 'store-header-group--sticky';
const ANIMATE = 'store-header-group--animate';

/**
 * Hides the header after it scrolls off, then slides it back when the page scrolls up.
 * Matches the live storefront: sticky only after the header has left the viewport.
 *
 * Scroll restoration (native on mobile, pageshow rAF in scroll-container.js on desktop)
 * can land the page past the header before any user scroll. Without an explicit reconcile,
 * the header stays in normal flow above the viewport — or a restoration jump is treated as
 * scrolling down and applies `hidden` — so the page opens with no header until scroll-up.
 */
class StoreHeaderSticky {
  /**
   * @param {HTMLElement} headerGroup
   */
  constructor(headerGroup) {
    this.headerGroup = headerGroup;
    this.headerBounds = { top: 0, bottom: 0 };
    this.currentScrollTop = 0;
    /** @type {EventTarget | null} */
    this.scrollTarget = null;
    /** @type {number | null} */
    this.rafId = null;
    /** @type {number | null} */
    this.motionFrame = null;
    this.followingMotion = false;

    this.onScroll = this.onScroll.bind(this);
    this.onHeaderTransition = this.onHeaderTransition.bind(this);
    this.onBreakpointChange = this.onBreakpointChange.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onPageShow = this.onPageShow.bind(this);
  }

  start() {
    this.syncAnnouncementOffset();
    this.measure();
    this.bindScroll();
    this.reconcile({ animate: false });
    scrollContainerMediaQuery.addEventListener('change', this.onBreakpointChange);
    window.addEventListener('resize', this.onResize);
    window.addEventListener('pageshow', this.onPageShow);
    const releaseIfStillInView = () => {
      this.syncAnnouncementOffset();
      if (!this.pastHeader()) this.reset();
      else this.syncVisibleHeader();
    };
    document.fonts?.ready.then(releaseIfStillInView);
    this.resizeObserver = new ResizeObserver(releaseIfStillInView);
    this.resizeObserver.observe(this.headerGroup);
    this.headerGroup.addEventListener('toggle', () => this.syncVisibleHeader(), true);
    this.headerGroup.addEventListener('transitionrun', this.onHeaderTransition);
    this.headerGroup.addEventListener('transitionend', this.onHeaderTransition);
    this.headerGroup.addEventListener('transitioncancel', this.onHeaderTransition);
    const search = this.headerGroup.querySelector('store-predictive-search');
    if (search) {
      this.searchObserver = new MutationObserver(() => this.syncVisibleHeader());
      this.searchObserver.observe(search, { attributes: true, attributeFilter: ['open'] });
    }
    this.syncVisibleHeader();
  }

  /**
   * Height of the header currently inside the scrollport.
   * A hidden header contributes nothing, including while its hide transition runs.
   */
  visibleHeaderHeight() {
    const searchOpen = Boolean(this.headerGroup.querySelector('store-predictive-search[open]'));
    if (this.headerGroup.classList.contains(HIDDEN) && !this.isOverlayOpen() && !searchOpen) return 0;

    const rect = this.headerGroup.getBoundingClientRect();
    const root = getIntersectionRoot();
    const bounds = root?.getBoundingClientRect();
    const portTop = bounds?.top ?? 0;
    const portBottom = bounds?.bottom ?? window.innerHeight;
    const top = Math.max(rect.top, portTop);
    const bottom = Math.min(rect.bottom, portBottom);
    return Math.max(0, Math.round(bottom - top));
  }

  syncVisibleHeader() {
    this.writeVisibleHeader();
  }

  writeVisibleHeader() {
    const next = `${this.visibleHeaderHeight()}px`;
    if (document.body.style.getPropertyValue('--header') === next) return;
    document.body.style.setProperty('--header', next);
  }

  /**
   * @param {TransitionEvent} event
   */
  onHeaderTransition(event) {
    if (event.target !== this.headerGroup || event.propertyName !== 'transform') return;
    if (event.type === 'transitionrun') {
      this.followHeaderMotion();
      return;
    }
    this.stopHeaderMotion();
  }

  followHeaderMotion() {
    if (this.followingMotion || !this.headerGroup.classList.contains(ANIMATE)) return;
    this.followingMotion = true;
    const started = performance.now();
    const step = (/** @type {number} */ now) => {
      if (!this.followingMotion) return;
      this.writeVisibleHeader();
      if (now - started > 200) {
        this.stopHeaderMotion();
        return;
      }
      this.motionFrame = requestAnimationFrame(step);
    };
    this.motionFrame = requestAnimationFrame(step);
  }

  stopHeaderMotion() {
    this.followingMotion = false;
    if (this.motionFrame !== null) {
      cancelAnimationFrame(this.motionFrame);
      this.motionFrame = null;
    }
    this.writeVisibleHeader();
  }

  syncAnnouncementOffset() {
    const announcement = this.headerGroup.querySelector('.store-header__announcement');
    const height = announcement ? Math.ceil(announcement.getBoundingClientRect().height) : 0;
    this.headerGroup.style.setProperty('--store-header-announcement-height', `${height}px`);
  }

  measure() {
    if (this.headerGroup.classList.contains(STICKY)) return;

    const scrollTop = getScrollTop();
    const rect = this.headerGroup.getBoundingClientRect();
    this.headerBounds = {
      top: Math.max(0, scrollTop + rect.top),
      bottom: scrollTop + rect.bottom,
    };
  }

  bindScroll() {
    this.scrollTarget?.removeEventListener('scroll', this.onScroll);
    this.scrollTarget = getScrollEventTarget();
    this.scrollTarget.addEventListener('scroll', this.onScroll, { passive: true });
    this.currentScrollTop = getScrollTop();
  }

  /**
   * Scroll offset where the header group has fully left the scrollport.
   * While in normal flow, uses live `offsetHeight` so a font load or announcement
   * wrap cannot leave `headerBounds.bottom` short and hide the header early.
   * Once sticky, stay sticky until scroll returns to the header's natural top.
   * Clearing sticky at `headerBounds.bottom` unparks the announcement mid-box and
   * jumps the bar from the sticky offset back into flow (the top-of-page stutter).
   * @param {number} [scrollTop]
   */
  pastHeader(scrollTop = getScrollTop()) {
    if (this.headerGroup.classList.contains(STICKY)) {
      return scrollTop > this.headerBounds.top;
    }

    this.measure();
    return scrollTop > this.headerBounds.top + this.headerGroup.offsetHeight;
  }

  /**
   * Align sticky/hidden classes with the current scroll position (no direction heuristic).
   * Used on start and after scroll restoration so mid-page entry still shows the header.
   * @param {{ animate?: boolean }} [options]
   */
  reconcile({ animate = false } = {}) {
    const scrollTop = getScrollTop();
    this.currentScrollTop = scrollTop;

    if (!this.pastHeader(scrollTop)) {
      this.reset();
      return;
    }

    this.reveal({ animate });
  }

  onPageShow() {
    // scroll-container.js restores desktop scroll in one rAF on pageshow; run after that.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.headerGroup.classList.remove(STICKY);
        this.syncAnnouncementOffset();
        this.measure();
        this.reconcile({ animate: false });
      });
    });
  }

  onBreakpointChange() {
    this.bindScroll();
    this.headerGroup.classList.remove(STICKY);
    this.measure();
    this.reconcile({ animate: false });
  }

  onResize() {
    this.syncAnnouncementOffset();
    this.measure();
    this.syncVisibleHeader();
  }

  onScroll() {
    if (this.rafId !== null) return;

    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      this.update();
    });
  }

  isOverlayOpen() {
    return Boolean(this.headerGroup.querySelector(':popover-open'));
  }

  isMobileSearchOpen() {
    return Boolean(this.headerGroup.querySelector('.store-header__bar--search-open'));
  }

  update() {
    const scrollTop = getScrollTop();
    const scrollingDown = scrollTop > this.currentScrollTop;
    const scrollingUp = scrollTop < this.currentScrollTop;

    if (!this.pastHeader(scrollTop)) {
      this.reset();
    } else if (scrollingUp) {
      this.reveal();
    } else if (scrollingDown && !this.isOverlayOpen() && !this.isMobileSearchOpen()) {
      this.hide();
    }

    this.currentScrollTop = scrollTop;
  }

  hide() {
    this.syncAnnouncementOffset();
    this.headerGroup.classList.add(HIDDEN, STICKY);
    this.syncVisibleHeader();
    this.followHeaderMotion();
  }

  /**
   * @param {{ animate?: boolean }} [options]
   */
  reveal({ animate = true } = {}) {
    this.headerGroup.classList.add(STICKY);
    this.headerGroup.classList.toggle(ANIMATE, animate);
    this.headerGroup.classList.remove(HIDDEN);
    this.syncVisibleHeader();
    this.followHeaderMotion();
  }

  reset() {
    this.stopHeaderMotion();
    this.headerGroup.classList.remove(HIDDEN, STICKY, ANIMATE);
    this.syncVisibleHeader();
  }
}

function initStoreHeaderSticky() {
  const headerGroup = document.getElementById('header-group');
  if (!headerGroup?.querySelector('.store-header')) return;
  if (headerGroup.dataset.storeHeaderSticky === 'ready') return;

  headerGroup.dataset.storeHeaderSticky = 'ready';
  new StoreHeaderSticky(headerGroup).start();
}

function bindLanguageSwitch(root) {
  if (!(root instanceof HTMLDetailsElement) || root.dataset.ready === 'true') return;
  root.dataset.ready = 'true';
  const summary = root.querySelector('summary');
  const sync = () => summary?.setAttribute('aria-expanded', root.open ? 'true' : 'false');
  sync();
  root.addEventListener('toggle', sync);

  const search = window.location.search;
  if (!search) return;
  root.querySelectorAll('a[href]').forEach((link) => {
    if (!(link instanceof HTMLAnchorElement)) return;
    const url = new URL(link.getAttribute('href') || '/', window.location.origin);
    if (url.search) return;
    link.href = `${url.pathname}${search}`;
  });
}

function initStoreHeaderMenu() {
  const menu = document.getElementById('StoreHeaderMenu');
  const header = menu?.closest('.store-header');
  const button = header?.querySelector('.store-header__menu-button');
  if (!(menu instanceof HTMLElement) || !(header instanceof HTMLElement) || !(button instanceof HTMLButtonElement)) return;
  if (menu.dataset.ready === 'true') return;
  menu.dataset.ready = 'true';

  const closeButton = menu.querySelector('[data-store-header-menu-close]');
  const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  let generation = 0;

  const isOpen = () => menu.matches(':popover-open');

  const sync = () => {
    button.setAttribute('aria-expanded', isOpen() && menu.classList.contains('is-open') ? 'true' : 'false');
  };

  const close = ({ focusButton = false } = {}) => {
    if (!isOpen() && !menu.classList.contains('is-open')) {
      sync();
      return;
    }

    const token = ++generation;
    menu.classList.remove('is-open');
    if (focusButton) button.focus();
    sync();

    const finish = () => {
      menu.removeEventListener('transitionend', onEnd);
      if (token !== generation) return;
      if (isOpen()) menu.hidePopover();
      sync();
    };

    const onEnd = (event) => {
      if (event.target !== menu || event.propertyName !== 'transform') return;
      finish();
    };

    if (motionQuery.matches) {
      finish();
      return;
    }

    menu.addEventListener('transitionend', onEnd);
    window.setTimeout(finish, 400);
  };

  const open = () => {
    const token = ++generation;
    if (!isOpen()) menu.showPopover();
    requestAnimationFrame(() => {
      if (token !== generation) return;
      menu.classList.add('is-open');
      sync();
    });
  };

  menu.storeHeaderMenuClose = close;

  button.addEventListener('click', (event) => {
    event.preventDefault();
    if (isOpen() && menu.classList.contains('is-open')) close();
    else open();
  });

  closeButton?.addEventListener('click', (event) => {
    event.preventDefault();
    close({ focusButton: true });
  });

  menu.querySelectorAll('[data-store-header-submenu]').forEach((button) => {
    if (!(button instanceof HTMLButtonElement)) return;
    const item = button.closest('.store-header__mobile-item');
    const submenu = item?.querySelector('.store-header__mobile-sub');
    if (!(item instanceof HTMLElement) || !(submenu instanceof HTMLElement)) return;

    let frame = 0;

    button.addEventListener('click', () => {
      const open = !item.classList.contains('is-open');
      const token = ++frame;
      const reduceMotion = motionQuery.matches;

      const from = submenu.getBoundingClientRect().height;
      submenu.style.height = `${from}px`;
      item.classList.toggle('is-open', open);
      button.setAttribute('aria-expanded', open ? 'true' : 'false');
      submenu.toggleAttribute('inert', !open);

      if (reduceMotion) {
        submenu.style.height = '';
        return;
      }

      const to = open ? submenu.scrollHeight : 0;

      requestAnimationFrame(() => {
        if (token !== frame) return;
        submenu.style.height = `${to}px`;
      });

      submenu.addEventListener('transitionend', function onEnd(event) {
        if (event.target !== submenu || event.propertyName !== 'height') return;
        submenu.removeEventListener('transitionend', onEnd);
        if (token !== frame) return;
        submenu.style.height = open ? '' : '0px';
      });
    });
  });

  menu.addEventListener('toggle', (event) => {
    if (event.target !== menu) return;
    if (!isOpen()) menu.classList.remove('is-open');
    sync();
  });

  sync();
}

function initStoreLanguageSwitch() {
  document.querySelectorAll('[data-store-language]').forEach((root) => {
    bindLanguageSwitch(root);
  });
}

function initStoreHeaderSearchToggle() {
  const header = document.querySelector('.store-header');
  const bar = header?.querySelector('.store-header__bar');
  const toggle = header?.querySelector('[data-store-header-search-toggle]');
  const tools = header?.querySelector('#StoreHeaderTools');
  const input = tools?.querySelector('input[type="search"]');
  const predictive = tools?.querySelector('store-predictive-search');
  if (
    !(header instanceof HTMLElement) ||
    !(bar instanceof HTMLElement) ||
    !(toggle instanceof HTMLButtonElement) ||
    !(tools instanceof HTMLElement)
  ) {
    return;
  }
  if (toggle.dataset.ready === 'true') return;
  toggle.dataset.ready = 'true';

  const openLabel = toggle.dataset.openLabel || toggle.getAttribute('aria-label') || '';
  const closeLabel = toggle.dataset.closeLabel || openLabel;
  const isDesktop = () => window.matchMedia('(min-width: 990px)').matches;

  const sync = (open) => {
    bar.classList.toggle('store-header__bar--search-open', open);
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    toggle.setAttribute('aria-label', open ? closeLabel : openLabel);
  };

  const closePredictive = () => {
    if (!(predictive instanceof HTMLElement)) return;
    predictive.removeAttribute('open');
  };

  const open = ({ focus = true } = {}) => {
    if (isDesktop()) return;
    sync(true);
    if (focus && input instanceof HTMLInputElement) {
      window.requestAnimationFrame(() => {
        try {
          input.focus({ preventScroll: true });
        } catch {
          input.focus();
        }
      });
    }
  };

  const close = ({ blur = true } = {}) => {
    sync(false);
    closePredictive();
    if (blur && input instanceof HTMLInputElement) input.blur();
  };

  toggle.addEventListener('click', () => {
    if (isDesktop()) return;
    if (bar.classList.contains('store-header__bar--search-open')) close();
    else open();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    if (!bar.classList.contains('store-header__bar--search-open') || isDesktop()) return;
    close();
    toggle.focus();
  });

  scrollContainerMediaQuery.addEventListener('change', (event) => {
    if (event.matches) close({ blur: false });
  });
}

if (document.documentElement.dataset.storeHeaderMenu !== 'true') {
  document.documentElement.dataset.storeHeaderMenu = 'true';

  document.addEventListener('click', (event) => {
    const menu = document.getElementById('StoreHeaderMenu');
    if (!(menu instanceof HTMLElement) || !menu.matches(':popover-open')) return;
    const target = event.target;
    if (!(target instanceof Node)) return;
    const button = menu.closest('.store-header')?.querySelector('.store-header__menu-button');
    if (menu.contains(target) || (button instanceof Node && button.contains(target))) return;
    if (typeof menu.storeHeaderMenuClose === 'function') menu.storeHeaderMenuClose();
    else menu.hidePopover();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const menu = document.getElementById('StoreHeaderMenu');
    if (!(menu instanceof HTMLElement) || !menu.matches(':popover-open')) return;
    if (typeof menu.storeHeaderMenuClose === 'function') {
      menu.storeHeaderMenuClose({ focusButton: true });
      return;
    }
    menu.hidePopover();
    menu.closest('.store-header')?.querySelector('.store-header__menu-button')?.focus();
  });
}

if (document.documentElement.dataset.storeLanguageSwitch !== 'true') {
  document.documentElement.dataset.storeLanguageSwitch = 'true';

  document.addEventListener('click', (event) => {
    const target = event.target;
    const inside = target instanceof Element ? target.closest('[data-store-language]') : null;
    document.querySelectorAll('[data-store-language][open]').forEach((node) => {
      if (node !== inside) node.removeAttribute('open');
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const open = document.querySelector('[data-store-language][open]');
    if (!(open instanceof HTMLDetailsElement)) return;
    open.open = false;
    open.querySelector('summary')?.focus();
  });
}

function initStoreHeaderAnnouncement(root = document) {
  const nodes = root.querySelectorAll('[data-store-header-announcement]');
  nodes.forEach((shell) => {
    if (!(shell instanceof HTMLElement) || shell.dataset.announcementReady === 'true') return;
    const track = shell.querySelector('[data-store-header-announcement-track]');
    if (!(track instanceof HTMLElement)) return;

    const slides = [...track.querySelectorAll('.store-header__announcement-text')];
    if (slides.length === 0) return;
    shell.dataset.announcementReady = 'true';
    if (slides.length < 2) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) return;

    const delay = Math.max(1200, Number(shell.dataset.delay || 3200));
    const speed = Math.max(0, Number(shell.dataset.speed || 500));
    // Seamless loop: append a clone of the first line after the last.
    track.appendChild(slides[0].cloneNode(true));
    const total = slides.length + 1;
    let index = 0;
    let timer = 0;
    let wrapping = false;

    const slideHeight = () =>
      track.querySelector('.store-header__announcement-text')?.getBoundingClientRect().height || 46;

    const goTo = (nextIndex, { animate = true } = {}) => {
      index = nextIndex;
      track.style.transition = animate ? `transform ${speed}ms ease` : 'none';
      track.style.transform = `translate3d(0, ${-index * slideHeight()}px, 0)`;
    };

    const tick = () => {
      if (wrapping) return;
      const next = index + 1;
      goTo(next, { animate: true });
      if (next === total - 1) {
        wrapping = true;
        window.setTimeout(() => {
          goTo(0, { animate: false });
          wrapping = false;
        }, speed + 30);
      }
    };

    const start = () => {
      window.clearInterval(timer);
      timer = window.setInterval(tick, delay);
    };

    goTo(0, { animate: false });
    start();

    shell.addEventListener('mouseenter', () => window.clearInterval(timer), { passive: true });
    shell.addEventListener('mouseleave', start, { passive: true });
  });
}

initStoreHeaderSticky();
initStoreHeaderMenu();
initStoreLanguageSwitch();
initStoreHeaderSearchToggle();
initStoreHeaderAnnouncement();

document.addEventListener('shopify:section:load', (event) => {
  const target = event.target;
  if (!(target instanceof Element) || !target.querySelector('.store-header')) return;
  initStoreHeaderSticky();
  initStoreHeaderMenu();
  initStoreLanguageSwitch();
  initStoreHeaderSearchToggle();
  initStoreHeaderAnnouncement();
});
